export default {};

function createWebGL() {
    const canvas = document.createElement('canvas');
    document.body.append(canvas);
    const dpr = Math.min(2, window.devicePixelRatio);
    canvas.width = (window.innerWidth * dpr) | 0;
    canvas.height = (window.innerHeight * dpr) | 0;
    canvas.style.width = window.innerWidth + 'px';
    canvas.style.height = window.innerHeight + 'px';
    const ctx = canvas.getContext('webgl', {
        alpha: false,
        depth: false,
        stencil: false,
        antialias: false,
        preserveDrawingBuffer: false
    });
    if (!ctx) throw new Error();
    return ctx;
}

const buffers: Array<ArrayBuffer> = [];
const bufferViews: Array<Uint8Array> = [];

const objs: Array<Array<{ rnd:number, idx:number, pg:number }>> = [];

class Test {
    readonly info: HTMLDivElement;
    size: number = 0;

    constructor(readonly type: string, x: number, y: number) {
        this.info = document.createElement("div");
        this.info.innerText = type;
        this.info.style.position = "absolute";
        this.info.style.left = `${x}px`;
        this.info.style.top = `${y}px`;
        this.info.style.width = `190px`;
        this.info.style.height = `40px`;
        this.info.style.border = "1px solid black";
        this.info.style.background = "white";
        this.info.onclick = () => {
            if (type == "JS") {
                this.allocHeap(1);
            }
            else if (type == "OBJ") {
                this.allocObjects(1);
            }
        };
        document.body.appendChild(this.info);

        const more5 = document.createElement("div");
        more5.innerText = "5x";
        more5.style.position = "absolute";
        more5.style.left = `${x + 200}px`;
        more5.style.top = `${y}px`;
        more5.style.width = `40px`;
        more5.style.height = `40px`;
        more5.style.border = "1px solid black";
        more5.style.background = "white";
        more5.onclick = () => {
            if (type == "JS") {
                this.allocHeap(5);
            }
            else if (type == "OBJ") {
                this.allocObjects(5);
            }
        };
        document.body.appendChild(more5);

        const more10 = document.createElement("div");
        more10.innerText = "10x";
        more10.style.position = "absolute";
        more10.style.left = `${x + 250}px`;
        more10.style.top = `${y}px`;
        more10.style.width = `40px`;
        more10.style.height = `40px`;
        more10.style.border = "1px solid black";
        more10.style.background = "white";
        more10.onclick = () => {
            if (type == "JS") {
                this.allocHeap(10);
            }
            else if (type == "OBJ") {
                this.allocObjects(10);
            }
        };
        document.body.appendChild(more10);
    }

    allocObjects(iterations:number) {
        for(let k = 0; k < iterations; ++k) {
            for (let i = 0; i < 10; ++i) {
                const arr = [];
                for (let j = 0; j < 100_000; ++j) {
                    arr.push({rnd: Math.random(), idx: j, pg: i});
                }
                objs.push(arr);
                this.size += 100_000;
            }
            this.info.innerText = `${this.type} ${(this.size / 1_000_000) | 0} Millions`;
        }
    }

    allocHeap(iterations:number) {
        for(let k = 0; k < iterations; ++k) {
            for (let i = 0; i < 10; ++i) {
                this.size += 10_000_000;
                this.info.innerText = `${this.type} ${(this.size / 1_000_000) | 0} MB`;
                const a = new ArrayBuffer(10_000_000);
                buffers.push(a);
                const v = new Uint8Array(a);
                for (let j = 0; j < v.length; ++j) {
                    v[j] = j & 0xFF;
                }
                bufferViews.push(v);
            }
        }
    }
}

const MEM = new Test("JS", 50, 100);
const MEM_COUNT = new Test("OBJ", 50, 150);
const MEM_TEX = new Test("TEX", 50, 200);
const MEM_NPOT = new Test("NPOT", 50, 250);

const GL = createWebGL();
GL.pixelStorei(GL.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1);
const textures: WebGLTexture[] = [];

const vsSource = `
attribute vec2 aVertexPosition;
attribute vec2 aVertexTexCoord;
varying mediump vec2 vTexCoord;
void main() {
  vTexCoord = aVertexTexCoord;
  gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
`;

const fsSource = `
uniform lowp sampler2D uSampler;
varying mediump vec2 vTexCoord;
void main() {
  lowp vec4 pixelColor = texture2D(uSampler, vTexCoord);
  gl_FragColor = pixelColor;
}
`;

//
// Initialize a shader program, so WebGL knows how to draw our data
//
function initShaderProgram(vsSource: string, fsSource: string) {
    const vertexShader = loadShader(GL.VERTEX_SHADER, vsSource)!;
    const fragmentShader = loadShader(GL.FRAGMENT_SHADER, fsSource)!;

    // Create the shader program

    const shaderProgram = GL.createProgram()!;
    GL.attachShader(shaderProgram, vertexShader);
    GL.attachShader(shaderProgram, fragmentShader);
    GL.linkProgram(shaderProgram);

    // If creating the shader program failed, alert

    if (!GL.getProgramParameter(shaderProgram, GL.LINK_STATUS)) {
        alert('Unable to initialize the shader program: ' + GL.getProgramInfoLog(shaderProgram));
        return null;
    }

    return shaderProgram;
}

//
// creates a shader of the given type, uploads the source and
// compiles it.
//
function loadShader(type: GLenum, source: string) {
    const shader = GL.createShader(type)!;

    // Send the source to the shader object

    GL.shaderSource(shader, source);

    // Compile the shader program

    GL.compileShader(shader);

    // See if it compiled successfully

    if (!GL.getShaderParameter(shader, GL.COMPILE_STATUS)) {
        alert('An error occurred compiling the shaders: ' + GL.getShaderInfoLog(shader));
        GL.deleteShader(shader);
        return null;
    }

    return shader;
}

const shaderProgram = initShaderProgram(vsSource, fsSource)!;
const programInfo = {
    program: shaderProgram,
    attribLocations: {
        vertexPosition: GL.getAttribLocation(shaderProgram, 'aVertexPosition'),
        vertexTexCoord: GL.getAttribLocation(shaderProgram, 'aVertexTexCoord'),
    },
    uniformLocations: {
        uSampler: GL.getUniformLocation(shaderProgram, 'uSampler'),
    },
};

const ib = GL.createBuffer();
GL.bindBuffer(GL.ELEMENT_ARRAY_BUFFER, ib);
GL.bufferData(GL.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 2, 3, 0]), GL.STATIC_DRAW);

const buffer = GL.createBuffer();
GL.bindBuffer(GL.ARRAY_BUFFER, buffer);
const buffer_data = [-1, -1, 0, 0, 1, -1, 1, 0, 1, 1, 1, 1, -1, 1, 0, 1];

GL.bufferData(GL.ARRAY_BUFFER, new Float32Array(buffer_data), GL.STATIC_DRAW);

function loop(ts: number) {
    const l = 0.9 + 0.1 * Math.random();
    GL.clearColor(l, l, l, 1);
    GL.clear(GL.COLOR_BUFFER_BIT);
    // GL.viewport(0, 0, GL.canvas.width, GL.canvas.height);
    GL.viewport(0, 0, 100, 100);

    for (const texture of textures) {
        GL.bindTexture(GL.TEXTURE_2D, texture);
    }
    let size = 0;
    for (const bv of bufferViews) {
        size += bv.length;
        bv[(Math.random() * bv.length) | 0] = (Math.random() * 255) | 0;
        const a = bv[(Math.random() * bv.length) | 0];
        bv[(Math.random() * bv.length) | 0] = a;
    }
    for(const o of objs) {
        let obj = o[(Math.random() * o.length) | 0];
        obj.idx = (obj.idx + 1) & 0x7FFFFFFF;
    }

    const actions = nextFrameActions;
    nextFrameActions = [];
    for (const action of actions) {
        action();
    }

    requestAnimationFrame(loop);
}

requestAnimationFrame(loop);

const imageSources: string[] = [];
const N = 100;
const format = 'png';
for (let i = 0; i < N; ++i) {
    imageSources.push(`bundle/atlas-${format}/${i}.${format}`);
}

const imageElements: HTMLImageElement[] = [];
const imageBitmaps: ImageBitmap[] = [];
const SHARED_IMG = document.createElement('img');

function loadTexture(src: string) {
    const img = document.createElement('img');
    img.onload = () => {
        console.log('loaded', img.src);
        const texture = GL.createTexture();
        if (texture) {
            GL.bindTexture(GL.TEXTURE_2D, texture);
            GL.texImage2D(GL.TEXTURE_2D, 0, GL.RGBA, GL.RGBA, GL.UNSIGNED_BYTE, img);
            textures.push(texture);
        }
        img.src = '';
    };
    img.src = src;
    return img;
}

function loadTextureSharedImageAsync(src: string) {
    return new Promise((resolve) => {
        const listener = (e: Event) => {
            console.log('loaded', src);
            const texture = GL.createTexture();
            if (texture) {
                GL.bindTexture(GL.TEXTURE_2D, texture);
                GL.texImage2D(GL.TEXTURE_2D, 0, GL.RGBA, GL.RGBA, GL.UNSIGNED_BYTE, SHARED_IMG);
                textures.push(texture);
            }
            // SHARED_IMG.onload = null;
            SHARED_IMG.src = '';
            SHARED_IMG.removeEventListener('load', listener, false);
            resolve();
        };
        SHARED_IMG.addEventListener('load', listener, false);
        SHARED_IMG.src = src;
    });
}

async function loadTextureDecodeAsync(src: string) {
    SHARED_IMG.decoding = 'async';
    SHARED_IMG.src = src;
    await SHARED_IMG.decode();
    console.log('loaded', src);
    const texture = GL.createTexture();
    if (texture) {
        GL.bindTexture(GL.TEXTURE_2D, texture);
        GL.texImage2D(GL.TEXTURE_2D, 0, GL.RGBA, GL.RGBA, GL.UNSIGNED_BYTE, SHARED_IMG);
        textures.push(texture);
    }
    SHARED_IMG.src = '';
}

async function loadTextures() {
    console.log('load textures');
    for (const src of imageSources) {
        // loadTexture(src);
        // await nextFrameAsync();
        // await loadTextureSharedImageAsync(src);
        // await nextFrameAsync();

        await Promise.all([
            loadTextureSharedImageAsync(src),
            nextFrameAsync
        ]);

        // await loadTextureDecodeAsync(src);
        // await nextFrameAsync();
    }
}

function loadImage(src: string): HTMLImageElement {
    const img = document.createElement('img');
    img.onload = () => {
        console.log('loaded', img.src);
    };
    img.src = src;
    return img;
}

function loadImageAsync(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = document.createElement('img');
        img.onload = () => {
            console.log('loaded', img.src);
            resolve(img);
        };
        img.src = src;
    });
}


let nextFrameActions: (() => void)[] = [];

function nextFrameAsync(): Promise<void> {
    return new Promise((resolve) => {
        nextFrameActions.push(() => resolve());
    });
}

async function loadImages() {
    console.log('load images');
    for (const src of imageSources) {
        const el = loadImage(src);
        imageElements.push(el);
    }
}

async function loadImageBitmaps() {
    console.log('load image bitmaps');
    for (const src of imageSources) {
        const el = await loadImageAsync(src);
        const bmp = await createImageBitmap(el, {premultiplyAlpha: "premultiply"});
        imageBitmaps.push(bmp);
    }
}


async function createTextures() {
    console.log('create textures');
    for (const el of imageElements) {
        const texture = GL.createTexture();
        if (texture) {
            GL.bindTexture(GL.TEXTURE_2D, texture);
            // GL.texImage2D(GL.TEXTURE_2D, 0, GL.RGBA, GL.RGBA, GL.UNSIGNED_BYTE, el);
            GL.texImage2D(GL.TEXTURE_2D, 0, GL.RGBA, GL.RGBA, GL.UNSIGNED_BYTE, el);
            textures.push(texture);
        }
        //await nextFrameAsync();
    }
}


async function createTexturesFromImageBitmaps() {
    console.log('create textures from ImageBitmap-s');
    for (const bmp of imageBitmaps) {
        const texture = GL.createTexture();
        if (texture) {
            GL.bindTexture(GL.TEXTURE_2D, texture);
            // GL.texImage2D(GL.TEXTURE_2D, 0, GL.RGBA, GL.RGBA, GL.UNSIGNED_BYTE, el);
            GL.texImage2D(GL.TEXTURE_2D, 0, GL.RGBA, GL.RGBA, GL.UNSIGNED_BYTE, bmp);
            textures.push(texture);
        }
        //await nextFrameAsync();
    }
}

function disposeImageBitmaps() {
    console.log('dispose image bitmaps');
    for (const bmp of imageBitmaps) {
        bmp.close();
        //el.remove();
    }
    imageBitmaps.length = 0;
}

function disposeImages() {
    console.log('dispose images');
    for (const el of imageElements) {
        el.src = '';
        //el.remove();
    }
    imageElements.length = 0;
}

// const STEPS = [loadImages, createTextures, disposeImages];
//const STEPS = [loadImages, createTextures];
const STEPS = [loadImageBitmaps, createTexturesFromImageBitmaps, disposeImageBitmaps];
// const STEPS = [loadTextures];

let currentStep = 0;
// GL.canvas.addEventListener('click', (e) => {
//     if (currentStep < STEPS.length) {
//         STEPS[currentStep++]();
//     }
// });

